import React, { useState, useEffect } from "react";
import { Drawer } from "@mui/material";
import { employeeHRApis, departmentHRApi } from "../../../Data/axios";

export default function RecHeader({ onFilterChange, filters }) {
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [recruiters, setRecruiters] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Default filter state
  const defaultFilters = {
    dateFilter: "Start Date",
    department: "All",
    status: "All",
    recruiter_name: "All",
    title: "",
  };

  // Check if any filters are applied (different from defaults)
  const hasActiveFilters = () => {
    return (
      filters?.dateFilter !== defaultFilters.dateFilter ||
      filters?.department !== defaultFilters.department ||
      filters?.status !== defaultFilters.status ||
      filters?.recruiter_name !== defaultFilters.recruiter_name ||
      filters?.title !== defaultFilters.title ||
      filters?.from_date ||
      filters?.to_date
    );
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    onFilterChange("clearAll", defaultFilters);
    setDateDropdownOpen(false);
    setShowCustomRange(false);
  };

  const handleFiltersOpen = () => {
    setFiltersOpen(true);
    console.log("filters ", filters);
  };

  const handleFiltersClose = () => {
    setFiltersOpen(false);
  };

  const handleFilterChange = (filterType, value) => {
    console.log(`Header filter changed - ${filterType}: ${value}`);
    onFilterChange(filterType, value);
  };

  const handleDrawerFilterChange = (filterType, value) => {
    console.log(`Drawer filter changed - ${filterType}: ${value}`);
    onFilterChange(filterType, value);
  };

  const handleDateRangeSelect = (range) => {
    console.log(`Date range filter changed: ${range}`);

    if (range === "custom") {
      setShowCustomRange(true);
      return;
    }

    let startDate, endDate;
    const today = new Date();

    switch (range) {
      case "today":
        startDate = endDate = today.toISOString().split("T")[0];
        break;
      case "last30days":
        endDate = today.toISOString().split("T")[0];
        startDate = new Date(today.setDate(today.getDate() - 30))
          .toISOString()
          .split("T")[0];
        break;
      case "thismonth":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split("T")[0];
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0)
          .toISOString()
          .split("T")[0];
        break;
      case "lastmonth":
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1)
          .toISOString()
          .split("T")[0];
        endDate = new Date(today.getFullYear(), today.getMonth(), 0)
          .toISOString()
          .split("T")[0];
        break;
      case "last6months":
        endDate = today.toISOString().split("T")[0];
        startDate = new Date(today.setMonth(today.getMonth() - 6))
          .toISOString()
          .split("T")[0];
        break;
      case "last1year":
        endDate = today.toISOString().split("T")[0];
        startDate = new Date(today.setFullYear(today.getFullYear() - 1))
          .toISOString()
          .split("T")[0];
        break;
      default:
        startDate = endDate = null;
    }

    if (startDate && endDate) {
      // Pass both dates in a single call to avoid async issues
      onFilterChange("dateRange", {
        from_date: startDate,
        to_date: endDate,
      });
    }
    setDateDropdownOpen(false);
    setShowCustomRange(false);
  };

  const handleCustomRangeApply = () => {
    if (customStartDate && customEndDate) {
      // Pass both dates in a single call
      onFilterChange("dateRange", {
        from_date: customStartDate,
        to_date: customEndDate,
      });
      setDateDropdownOpen(false);
      setShowCustomRange(false);
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const getDateRangeLabel = (range) => {
    switch (range) {
      case "today":
        return "Today";
      case "last30days":
        return "Last 30 Days";
      case "thismonth":
        return "This Month";
      case "lastmonth":
        return "Last Month";
      case "last6months":
        return "Last 6 Months";
      case "last1year":
        return "Last 1 Year";
      default:
        return "Start Date To End Date";
    }
  };

  const getDisplayText = () => {
    if (filters?.from_date && filters?.to_date) {
      return `${filters.from_date} - ${filters.to_date}`;
    }
    return "Start Date To End Date";
  };

  const handleRecruiterChange = (e) => {
    const value = e.target.value;
    console.log(`Recruiter filter changed: ${value}`);
    onFilterChange("recruiter_name", value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    console.log(`Search filter changed: ${value}`);
    onFilterChange("title", value);
  };

  // Fetch recruiters and departments on component mount
  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const response = await employeeHRApis.getAll();
        console.log("API Response for recruiters:", response);

        // Assuming the response contains an array of employees
        // You might need to adjust the property name based on actual API response structure
        const recruiterList = response.data || response || [];
        console.log("Recruiter list:", recruiterList);

        setRecruiters(recruiterList);
      } catch (error) {
        console.error("Error fetching recruiters:", error);
        setRecruiters([]);
      }
    };

    const fetchDepartments = async () => {
      try {
        const response = await departmentHRApi.getAll();
        console.log("API Response for departments:", response);

        // Assuming the response contains an array of departments
        // You might need to adjust the property name based on actual API response structure
        const departmentList = response.data || response || [];
        console.log("Department list:", departmentList);

        setDepartments(departmentList);
      } catch (error) {
        console.error("Error fetching departments:", error);
        setDepartments([]);
      }
    };

    fetchRecruiters();
    fetchDepartments();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dateDropdownOpen &&
        !event.target.closest(".date-dropdown-container")
      ) {
        setDateDropdownOpen(false);
        setShowCustomRange(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dateDropdownOpen]);

  return (
    <>
      <div className="w-full bg-white shadow-sm mb-6 border-b border-gray-200 font-ubuntu">
        <div className="container mx-auto py-3 px-4">
          <div className="flex items-center gap-x-3">
            {/* Duration dropdown */}
            <div className="relative">
              <div className="flex items-center text-gray-600 text-sm font-normal hover:text-gray-800 focus:outline-none">
                <span>Duration</span>
              </div>
              <div className="absolute hidden bg-white shadow-md rounded-md mt-2 w-48 py-1 z-10">
                {/* Dropdown content would go here */}
              </div>
            </div>

            {/* Date range section */}
            <div className="flex items-center relative date-dropdown-container">
              <button
                className="text-gray-400 text-sm font-medium flex items-center hover:text-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setDateDropdownOpen(!dateDropdownOpen);
                }}
              >
                {getDisplayText()}
                <svg
                  className="w-4 h-4 ml-1"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Date range dropdown */}
              {dateDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 py-2 z-50 min-w-[200px]">
                  {!showCustomRange ? (
                    <>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => handleDateRangeSelect("today")}
                      >
                        Today
                      </button>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => handleDateRangeSelect("last30days")}
                      >
                        Last 30 Days
                      </button>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => handleDateRangeSelect("thismonth")}
                      >
                        This Month
                      </button>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => handleDateRangeSelect("lastmonth")}
                      >
                        Last Month
                      </button>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => handleDateRangeSelect("last6months")}
                      >
                        Last 6 Months
                      </button>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => handleDateRangeSelect("last1year")}
                      >
                        Last 1 Year
                      </button>
                      <hr className="my-1" />
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => handleDateRangeSelect("custom")}
                      >
                        Custom Range
                      </button>
                      {/* Clear Filter button */}
                      {filters?.to_date && (
                        <>
                          <hr className="my-1" />
                          <button
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            onClick={() => {
                              // Clear both dates in a single call
                              onFilterChange("dateRange", {
                                from_date: null,
                                to_date: null,
                              });
                              setDateDropdownOpen(false);
                            }}
                          >
                            Clear Filter
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="px-4 py-3">
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 text-black"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 text-black"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleCustomRangeApply}
                          disabled={!customStartDate || !customEndDate}
                          className="flex-1 bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          Apply
                        </button>
                        <button
                          onClick={() => setShowCustomRange(false)}
                          className="flex-1 bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-xs font-medium hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Vertical divider - taller to override padding */}
            <div className="h-8 w-px bg-gray-300 my-auto ml-5"></div>

            {/* Recruiter section with dropdown */}
            <div className="flex items-center">
              <span className="text-gray-600 text-sm font-medium mr-2">
                Recruiter
              </span>
              <div className="inline-block relative">
                <div className="flex items-center bg-white border border-gray-300 rounded py-1 px-2 text-sm">
                  <select
                    className="w-full border-none text-gray-900 appearance-none bg-white focus:outline-none"
                    value={filters?.recruiter_name || "All"}
                    onChange={handleRecruiterChange}
                  >
                    <option value="All">All</option>
                    {recruiters.map((recruiter) => (
                      <option
                        key={recruiter.empid || recruiter._id}
                        value={recruiter.name || recruiter._id}
                      >
                        {recruiter.name}
                      </option>
                    ))}
                  </select>
                  <svg
                    className="w-4 h-4 ml-1"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Vertical divider - taller to override padding */}
            <div className="h-8 w-px bg-gray-300 my-auto"></div>

            {/* Search input */}
            <div className="relative">
              <div className="flex items-center border border-gray-300 rounded bg-gray-200">
                <div className="pl-2 pr-2">
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Start typing to search"
                  className="py-1 px-2 text-sm w-72 border-none rounded-none text-black focus:outline-none"
                  value={filters?.title || ""}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            {/* Clear All Filters button - only show when filters are active */}
            {hasActiveFilters() && (
              <>
                <div className="h-8 w-px bg-gray-300 my-auto"></div>
                <button
                  onClick={handleClearAllFilters}
                  className="flex items-center text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Clear All
                </button>
              </>
            )}

            {/* Filters button */}
            <div className="ml-auto">
              <button
                className="flex items-center text-gray-600 hover:text-gray-800"
                onClick={handleFiltersOpen}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm font-medium">Filters</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Drawer */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={handleFiltersClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            padding: "24px",
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
          },
        }}
      >
        <div className="p-6 bg-white">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-medium text-gray-800">Filters</h2>
            <button
              onClick={handleFiltersClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Date Filter */}
            <div>
              <p className="text-gray-600 mb-3">Date Filter On</p>
              <div className="relative">
                <select
                  className="w-full p-2.5 border border-gray-300 rounded text-gray-900 appearance-none bg-white focus:outline-none focus:border-blue-500"
                  value={filters?.dateFilter || "Start Date"}
                  onChange={(e) =>
                    handleDrawerFilterChange("dateFilter", e.target.value)
                  }
                >
                  <option>Start Date</option>
                  <option>End Date</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Department */}
            <div>
              <p className="text-gray-600 mb-3">Department</p>
              <div className="relative">
                <select
                  className="w-full p-2.5 border border-gray-300 rounded text-gray-900 appearance-none bg-white focus:outline-none focus:border-blue-500"
                  value={filters?.department || "All"}
                  onChange={(e) =>
                    handleDrawerFilterChange("department", e.target.value)
                  }
                >
                  <option>All</option>
                  {departments.map((department) => (
                    <option
                      key={department.id || department._id}
                      value={
                        department.department || department.department_name
                      }
                    >
                      {department.department || department.department_name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <p className="text-gray-600 mb-3">Status</p>
              <div className="relative">
                <select
                  className="w-full p-2.5 border border-gray-300 rounded text-gray-900 appearance-none bg-white focus:outline-none focus:border-blue-500"
                  value={filters?.status || "All"}
                  onChange={(e) =>
                    handleDrawerFilterChange("status", e.target.value)
                  }
                >
                  <option>All</option>
                  <option>Open</option>
                  <option>Closed</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
}
